import React, { useState, useEffect } from "react";
import Measure from "react-measure";
import classnames from "classnames";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { messages } from "../../../i18n-messages";
import { SelectInput } from "~/core";
import { AgEventAPI, ISelectOption } from "@ai360/core";
import { ACTIVE_YN } from "~/core/picklist";
import "./sample-point-table.css";

import { updateSampleLabelSequence, isSampleLabelComposite } from "@ai360/core";
import { usePrevious } from "~/action-panel/components/common/custom-hooks";

interface IProductivityRatingCellProps {
    intl: intlShape;
    onSetProdRating: (value) => void;
    productivityRatingGuid: string;
    productivityRatingOptions: ISelectOption<string>[];
}

function ProductivityRatingCell(props: IProductivityRatingCellProps) {
    const { productivityRatingGuid, productivityRatingOptions, onSetProdRating } = props;
    const { formatMessage } = props.intl;

    return (
        <div onClick={(evt) => evt.preventDefault()}>
            <SelectInput
                maxHeight={500}
                optionIsHiddenKey={ACTIVE_YN}
                minOptionsWidth={175}
                onChange={(val) => onSetProdRating(val)}
                options={productivityRatingOptions}
                placeholderText={formatMessage(messages.ratingTxt)}
                value={productivityRatingGuid}
            />
        </div>
    );
}

interface ISamplePointTableProps {
    depthConfig: AgEventAPI.IDepthConfigDetail[];
    intl: intlShape;
    onSetPointProdRating: (pointIndex, val) => void;
    onSetSelectedPointSet: (pointSet) => void;
    productivityRatingOptions: ISelectOption<string>[];
    samplePoints: AgEventAPI.ISoilSamplePoint[];
    selectedPointSet: Set<string>;
    showProductivityRating: boolean;
}

function SamplePointTable_(props: ISamplePointTableProps) {
    const [colWidths, setColWidths] = useState([]);
    const [focusedRatingInput, setFocusedRatingInput] = useState(null);
    const [isComposite, setIsComposite] = useState(isSampleLabelComposite(props.samplePoints));
    const [samplePointsSequenceMap, setSamplePointSequenceMap] = useState(new Map());
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);

    let lastClickedWithoutShiftIndex = null;

    const previousProps = usePrevious({ samplePoints: props.samplePoints });

    useEffect(() => {
        const samplePointsChanged = previousProps?.samplePoints !== props.samplePoints;
        if (samplePointsChanged) {
            setIsComposite(isSampleLabelComposite(props.samplePoints));
            updateSampleLabelSequence(samplePointsSequenceMap, props.samplePoints);
            setSamplePointSequenceMap(samplePointsSequenceMap);
        }
    }, [props.samplePoints]);

    const _getColumnHorizontalPos = (columnIndex, isStickyCol) => {
        const prevColumnsWidth = colWidths
            .slice(0, columnIndex)
            .reduce((accum, width) => accum + width, 0);
        if (!isStickyCol) {
            return prevColumnsWidth;
        }
        return prevColumnsWidth + scrollLeft;
    };

    const _getDepthConfigRange = (depthConfigIndex) => {
        const depthConfig = props.depthConfig[depthConfigIndex];
        return (
            <div className="body-text">{`${depthConfig.startDepth} - ${depthConfig.endDepth}`}</div>
        );
    };

    const _getHeader = (isStickyRow) => {
        const { depthConfig, showProductivityRating } = props;
        const { formatMessage } = props.intl;

        const stickyColCount = showProductivityRating ? 2 : 1;

        const columnIndices = Array.from(
            { length: depthConfig.length + stickyColCount },
            (_, idx) => idx
        );
        const headerCells = columnIndices.map((columnIndex) => {
            const isStickyCol = columnIndex < stickyColCount;
            const className = classnames(
                {
                    "prod-rating": showProductivityRating && columnIndex === 1,
                    "sticky-col": isStickyCol && !isStickyRow,
                    sticky: isStickyRow && !isStickyCol,
                    "sticky-all": isStickyRow && isStickyCol,
                },
                "header-cell"
            );

            if (!isStickyRow) {
                const spacerCell = <div className={className}>{columnIndex}</div>;
                return (
                    <Measure
                        key={`header_${columnIndex}`}
                        onMeasure={({ width }) => _setColWidth(columnIndex, width)}
                    >
                        {spacerCell}
                    </Measure>
                );
            }

            const stickyStyle = {
                left: _getColumnHorizontalPos(columnIndex, isStickyCol),
                top: scrollTop,
                width: colWidths[columnIndex],
            };
            return (
                <div key={`header_${columnIndex}`} className={className} style={stickyStyle}>
                    {columnIndex >= stickyColCount
                        ? depthConfig.length > 1
                            ? depthConfig[columnIndex - stickyColCount].depthId
                            : formatMessage(messages.depthColLabel)
                        : columnIndex === 0
                        ? formatMessage(messages.idColLabel)
                        : formatMessage(messages.ratingTxt)}
                </div>
            );
        });

        return <div className="header">{headerCells}</div>;
    };

    const _getRow = (samplePoint, pointIndex) => {
        const { depthConfig, selectedPointSet, showProductivityRating } = props;
        const stickyColCount = showProductivityRating ? 2 : 1;

        const columnIndices = Array.from(
            { length: depthConfig.length + stickyColCount },
            (_, idx) => idx
        );
        const rowCells = columnIndices.map((columnIndex) => {
            const isStickyCol = columnIndex < stickyColCount;
            const className = classnames(
                {
                    "fixed-container": focusedRatingInput === pointIndex,
                    "prod-rating": showProductivityRating && columnIndex === 1,
                    selected: selectedPointSet.has(samplePoint.soilSamplePointGuid),
                    "sticky-col": isStickyCol,
                    sticky: isStickyCol,
                },
                "body-cell"
            );

            const onClick = (event) => {
                if (event.defaultPrevented) {
                    return;
                }
                _selectPoints(pointIndex, event.ctrlKey, event.shiftKey);
            };

            const handleFocus = showProductivityRating && columnIndex === 1;
            const onFocus = !handleFocus
                ? null
                : () => {
                      setFocusedRatingInput(pointIndex);
                  };
            const onBlur = !handleFocus
                ? null
                : () => {
                      setFocusedRatingInput(null);
                  };

            const stickyStyle = !isStickyCol
                ? null
                : {
                      left: _getColumnHorizontalPos(columnIndex, isStickyCol),
                      width: colWidths[columnIndex],
                  };

            return (
                <div
                    key={`${samplePoint.soilSamplePointGuid}_${columnIndex}`}
                    className={className}
                    onBlur={onBlur}
                    onClick={onClick}
                    onFocus={onFocus}
                    style={stickyStyle}
                >
                    {columnIndex >= stickyColCount
                        ? _getDepthConfigRange(columnIndex - stickyColCount)
                        : columnIndex === 0
                        ? _getSamplePointId(samplePoint)
                        : _getSampleProductivityRating(samplePoint, pointIndex)}
                </div>
            );
        });

        for (let columnIndex = 0; columnIndex < stickyColCount; columnIndex++) {
            const className = classnames("body-cell", {
                "prod-rating": showProductivityRating && columnIndex === 1,
            });
            rowCells.splice(
                0,
                0,
                <div
                    key={`${samplePoint.soilSamplePointGuid}_flexspacer_${columnIndex}`}
                    className={className}
                />
            );
        }

        return (
            <div key={`${samplePoint.soilSamplePointGuid}`} className="row">
                {rowCells}
            </div>
        );
    };

    const _getSamplePointId = (samplePoint) => {
        const seqId = samplePointsSequenceMap.get(samplePoint.soilSamplePointGuid);
        return (
            <div className="body-text">
                {samplePoint.sequenceId == null
                    ? "-"
                    : isComposite
                    ? `${samplePoint.sampleId} - ${seqId}`
                    : `${samplePoint.sampleId}`}
            </div>
        );
    };

    const _getSampleProductivityRating = (samplePoint, pointIndex) => {
        const { intl, onSetPointProdRating, productivityRatingOptions } = props;
        return (
            <ProductivityRatingCell
                intl={intl}
                onSetProdRating={(val) => onSetPointProdRating(pointIndex, val)}
                productivityRatingGuid={samplePoint.productivityRatingGuid}
                productivityRatingOptions={productivityRatingOptions}
            />
        );
    };

    const _onScroll = (evt) => {
        evt.stopPropagation();
        evt.preventDefault();

        const { target } = evt;
        setScrollLeft(target.scrollLeft);
        setScrollTop(target.scrollTop);
    };

    const _selectPoints = (pointIndex, withCtrl, withShift) => {
        const { samplePoints, selectedPointSet } = props;
        const noModifiers = !withCtrl && !withShift;

        const newSelectedPointSet = new Set();
        if (noModifiers) {
            newSelectedPointSet.add(samplePoints[pointIndex].soilSamplePointGuid);
        } else if (withCtrl) {
            [...selectedPointSet].forEach((soilSamplePointGuid) =>
                newSelectedPointSet.add(soilSamplePointGuid)
            );

            const toggleSamplePoint = samplePoints[pointIndex];
            if (selectedPointSet.has(toggleSamplePoint.soilSamplePointGuid)) {
                newSelectedPointSet.delete(toggleSamplePoint.soilSamplePointGuid);
            } else {
                newSelectedPointSet.add(toggleSamplePoint.soilSamplePointGuid);
            }
        } else {
            console.assert(withShift);

            // Select all options between this option and the last non-select-click
            if (lastClickedWithoutShiftIndex == null) {
                lastClickedWithoutShiftIndex = 0;
            }

            samplePoints
                .slice(
                    Math.min(lastClickedWithoutShiftIndex, pointIndex),
                    Math.max(lastClickedWithoutShiftIndex, pointIndex) + 1
                )
                .forEach((samplePoint) => newSelectedPointSet.add(samplePoint.soilSamplePointGuid));
        }

        if (!withShift) {
            lastClickedWithoutShiftIndex = pointIndex;
        }

        props.onSetSelectedPointSet(newSelectedPointSet);
    };

    const _setColWidth = (columnIndex, width) => {
        if (colWidths[columnIndex] === width) {
            return;
        }
        colWidths[columnIndex] = width;
        setColWidths([...colWidths]);
    };

    const { samplePoints } = props;
    return (
        <div className="sample-point-tbl" onScroll={(evt) => _onScroll(evt)}>
            {_getHeader(false)}
            {_getHeader(true)}
            {samplePoints.map((point, pointIdx) => _getRow(point, pointIdx))}
        </div>
    );
}

export const SamplePointTable = injectIntl(SamplePointTable_);
